import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiUser, AccountList } from '../../types/accounts';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './Accounts.scss';

// Helper function to check if a user is a system user (only filter out obvious system accounts)
const isSystemUser = (user: ApiUser): boolean => {
  if (!user || !user.id) return true;
  // Only filter out the null UUID system account
  if (user.id === '00000000-0000-0000-0000-000000000000') return true;
  return false;
};

// Filter function to get only real player accounts (exclude system accounts only)
const filterRealPlayers = (users: ApiUser[]): ApiUser[] => {
  return users.filter(user => user && !isSystemUser(user));
};

const Accounts: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [accounts, setAccounts] = useState<ApiUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [displayCount, setDisplayCount] = useState({ from: 1, to: 0 });
  const [nextCursor, setNextCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [includeTombstones, setIncludeTombstones] = useState(false);

  const sessionRole = authService.getSessionRole();
  const canDelete = sessionRole === UserRole.USER_ROLE_ADMIN;

  useEffect(() => {
    const cursor = searchParams.get('cursor') || '';
    const filter = searchParams.get('filter') || '';
    const tombstones = searchParams.get('tombstones') === 'true';
    
    setSearchFilter(filter);
    setIncludeTombstones(tombstones);
    loadAccounts(cursor, filter, tombstones);
  }, []);

  const loadAccounts = useCallback(async (cursor?: string, filter?: string, tombstones?: boolean) => {
    setLoading(true);
    setError('');

    try {
      const result: AccountList = await consoleService.listAccounts(
        '', 
        filter || undefined, 
        tombstones || false, 
        cursor || null
      );

      const allUsers = result.users || [];
      const realPlayers = filterRealPlayers(allUsers);
      
      setAccounts(realPlayers);
      setTotalCount(result.total_count || realPlayers.length);
      setDisplayCount({
        from: cursor ? displayCount.to + 1 : 1,
        to: cursor ? displayCount.to + realPlayers.length : realPlayers.length
      });
      setNextCursor(result.next_cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [displayCount.to]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchFilter) params.set('filter', searchFilter);
    if (includeTombstones) params.set('tombstones', 'true');
    setSearchParams(params);
    setDisplayCount({ from: 1, to: 0 });
    
    // Auto-wrap search term with wildcards if user didn't include them
    let filterValue = searchFilter;
    if (filterValue && !filterValue.includes('%')) {
      filterValue = `%${filterValue}%`;
    }
    
    loadAccounts(undefined, filterValue, includeTombstones);
  };

  const handleNextPage = () => {
    if (nextCursor) {
      const params = new URLSearchParams();
      if (searchFilter) params.set('filter', searchFilter);
      if (includeTombstones) params.set('tombstones', 'true');
      params.set('cursor', nextCursor);
      setSearchParams(params);
      loadAccounts(nextCursor, searchFilter, includeTombstones);
    }
  };

  const handleFirstPage = () => {
    const params = new URLSearchParams();
    if (searchFilter) params.set('filter', searchFilter);
    if (includeTombstones) params.set('tombstones', 'true');
    setSearchParams(params);
    setDisplayCount({ from: 1, to: 0 });
    loadAccounts(undefined, searchFilter, includeTombstones);
  };

  const handleViewPlayer = (user: ApiUser) => {
    navigate(`/accounts/${user.id}`);
  };

  const handleDeletePlayer = (user: ApiUser, index: number) => {
    openConfirmDialog(
      'Delete Player',
      `Are you sure you want to delete "${user.username || user.display_name || user.id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteAccount('', user.id, false);
          setAccounts(prev => prev.filter((_, i) => i !== index));
          setTotalCount(prev => prev - 1);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete player');
        }
      }
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="players-container">
      {/* Header */}
      <div className="players-header">
        <div className="header-text">
          <h1 className="players-title">Players</h1>
          <p className="players-subtitle">
            Every player is registered and has a profile for other users to find and become friends with or join groups and chat.
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search by username, user ID, email, or display name..."
            />
          </div>
          
          <div className="search-options">
            <label className="tombstones-toggle">
              <span>Tombstones</span>
              <input
                type="checkbox"
                checked={includeTombstones}
                onChange={(e) => setIncludeTombstones(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="search-info">
            <span className="count-info">
              {displayCount.to > 0 ? `${displayCount.from} to ${displayCount.to} of ${totalCount}` : `${accounts.length} players`}
            </span>
            <div className="pagination-buttons">
              <button 
                type="button" 
                className="btn-page" 
                onClick={handleFirstPage}
                disabled={loading}
                title="First page"
              >
                ⟨⟨
              </button>
              <button 
                type="button" 
                className="btn-page" 
                onClick={handleNextPage}
                disabled={!nextCursor || loading}
                title="Next page"
              >
                ⟩
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger">
          <span>⚠️ {error}</span>
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Players Table */}
      <div className="players-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading players...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="players-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Username</th>
                  <th>Display Name</th>
                  <th>Updated</th>
                  {canDelete && <th style={{ width: '80px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan={canDelete ? 5 : 4} className="empty-state">
                      <p>No players found</p>
                      <small>Try adjusting your search filter</small>
                    </td>
                  </tr>
                ) : (
                  accounts.map((user, index) => (
                    <tr key={user.id} onClick={() => handleViewPlayer(user)}>
                      <td className="user-id-cell">
                        <code>{user.id}</code>
                      </td>
                      <td className="username-cell">{user.username || '-'}</td>
                      <td className="display-name-cell">{user.display_name || '-'}</td>
                      <td className="date-cell">{formatDate(user.update_time)}</td>
                      {canDelete && (
                        <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeletePlayer(user, index)}
                            title="Delete player"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
        variant="danger"
      />
    </div>
  );
};

export default Accounts;
